import { useCallback, useEffect, useMemo, useState } from "react";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import LoadingState from "../../components/ui/loading-state";
import { TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta, KartuBarisAksi } from "../../components/ui/responsive-table";
import { formatTanggal } from "../../lib/tanggal";
import { BatasDaftarNotice } from "./_shared";
import { Link } from "react-router-dom";
import { FileSpreadsheet, RefreshCw, CheckCircle2, Upload, AlertTriangle, Percent, Tags, Settings2, Printer } from "lucide-react";
import ClaimMdrImporModal from "./ClaimMdrImporModal";
import ClaimMdrLeasingModal from "./ClaimMdrLeasingModal";
import ClaimMdrNotaPrint from "./ClaimMdrNotaPrint";
import { MDR_BASE, MODUL_MDR, SIAP_CLAIM, SUDAH_CLAIM, nilaiMdr } from "../../lib/claimMdr";


// Backend memotong daftar baris di 500 (`claim_mdr.baris_tab`). Dipakai
// `BatasDaftarNotice` supaya tidak ada yang mengira daftarnya lengkap.
const BATAS_BARIS = 500;

// Warna per KODE status. Kode stabil, kalimatnya tidak — jadi yang diketik di
// sini hanya kodenya, labelnya tetap datang dari server. Status yang belum
// dikenal jatuh ke abu-abu dan tetap tampil dengan label servernya, bukan
// hilang dari layar.
const WARNA_STATUS = {
  eligible: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sudah_claim: "bg-sky-50 text-sky-700 border-sky-200",
  perlu_review: "bg-amber-50 text-amber-700 border-amber-200",
  tidak_eligible: "bg-rose-50 text-rose-700 border-rose-200",
};

function Pil({ kode, label }) {
  const w = WARNA_STATUS[kode] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${w}`}>
      {label || kode || "Belum dicocokkan"}
    </span>
  );
}

export default function ClaimMdr() {
  const { can } = useAuth();
  const bolehLihat = can(MODUL_MDR, "view");
  const bolehUbah = can(MODUL_MDR, "update");

  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reload, setReload] = useState(0);
  const muatUlang = useCallback(() => setReload((n) => n + 1), []);

  const [pilih, setPilih] = useState(() => new Set());
  const [sibuk, setSibuk] = useState("");          // "cocok" | "ekspor" | "tandai"
  const [ringkas, setRingkas] = useState(null);    // hasil cocokkan terakhir
  const [gagal, setGagal] = useState([]);          // baris yang GAGAL ditandai
  const [buka, setBuka] = useState(false);         // modal impor
  const [bukaLeasing, setBukaLeasing] = useState(false);
  const [notaBaris, setNotaBaris] = useState(null);   // baris yang notanya dicetak

  // Kosakata (tab, status, alasan, nama medan) datang dari server — lihat
  // `claim_mdr.meta`. Layar tidak menyimpan salinannya.
  useEffect(() => {
    if (!bolehLihat) return;
    let batal = false;
    api.get(`${MDR_BASE}/meta`)
      .then((r) => {
        if (batal) return;
        setMeta(r.data);
        setTab((t) => t || r.data?.tab?.[0]?.kode || null);
      })
      .catch((e) => {
        if (!batal) toast.error(e?.response?.data?.detail || "Gagal memuat kosakata Claim MDR");
      });
    return () => { batal = true; };
  }, [bolehLihat]);

  useEffect(() => {
    if (!bolehLihat || !tab) return;
    let batal = false;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const r = await api.get(`${MDR_BASE}/tab/${tab}/baris`, { params: { limit: BATAS_BARIS } });
        if (batal) return;
        setItems(r.data.items || []);
      } catch (e) {
        if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat Data Claim"); }
      } finally { if (!batal) setLoading(false); }
    })();
    return () => { batal = true; };
  }, [bolehLihat, tab, reload]);

  // Pindah tab = keadaan lain sama sekali (butir 24). Pilihan, ringkasan, dan
  // daftar gagal milik tab sebelumnya tidak boleh ikut terbawa — menandai
  // baris yang dipilih di tab lain akan mengunci data PP untuk tab yang salah.
  useEffect(() => { setPilih(new Set()); setRingkas(null); setGagal([]); }, [tab]);

  const labelStatus = useMemo(
    () => Object.fromEntries((meta?.status || []).map((s) => [s.kode, s.label])), [meta]);
  const namaLeasing = useMemo(
    () => Object.fromEntries((meta?.leasing || []).map((l) => [l.kode, l.nama])), [meta]);
  const pesanAlasan = meta?.alasan || {};
  const namaMedan = meta?.medan || {};

  const siap = useMemo(() => items.filter((b) => b.status === SIAP_CLAIM), [items]);
  const terpilih = useMemo(() => siap.filter((b) => pilih.has(b.kunci)), [siap, pilih]);

  const togglePilih = (kunci) => setPilih((s) => {
    const n = new Set(s);
    if (n.has(kunci)) n.delete(kunci); else n.add(kunci);
    return n;
  });
  const toggleSemua = () => setPilih((s) =>
    s.size >= siap.length ? new Set() : new Set(siap.map((b) => b.kunci)));

  /** Tenor apa adanya — berkas leasing menulisnya bebas ("24 BULAN", "12", "24x").
   *
   * Menambahkan "x" tanpa melihat isinya melahirkan "24 BULANx" — terbukti saat
   * menulis tes untuk kolom ini, karena fixture-nya memakai bentuk teks. Imbuhan
   * hanya dipasang kalau nilainya MURNI angka; selebihnya ditampilkan seperti
   * yang Finance tulis, supaya layar tidak mengarang satuan yang bukan dari
   * berkasnya. */
  const teksTenor = (b) => {
    const v = nilaiMdr(b, "tenor");
    if (v == null || String(v).trim() === "") return "—";
    const s = String(v).trim();
    return /^\d+$/.test(s) ? `${s}x` : s;
  };

  /** Medan yang WAJIB ada sebelum notanya boleh dicetak (butir 28 a–e).
   *
   * Bukan `MEDAN_WAJIB_ELIGIBLE`: nota tidak butuh SRP/DP, tapi nota yang
   * IMEI atau nama konsumennya kosong adalah dokumen yang diberikan ke orang
   * dengan lubang di tengahnya. Lebih baik tombolnya mati dengan sebab yang
   * tertulis daripada mencetak nota separuh. */
  const medanKurangNota = (b) => ["tanggal_pembelian", "nama_konsumen", "tipe_produk",
                                  "imei", "sn", "nominal"]
    .filter((m) => {
      const v = nilaiMdr(b, m);
      return v === undefined || v === null || v === "";
    });

  /** Sebab sebuah baris tidak bisa dipilih — kalimatnya SAMA dengan yang
   *  dipakai backend menolak (`/meta` + `proses.tandai_sudah_claim`), supaya
   *  yang dibaca sebelum mengklik sama dengan sesudah mengklik. */
  const sebabTidakBisaDipilih = (b) => {
    if (b.status === SIAP_CLAIM) return "";
    if (b.status === SUDAH_CLAIM) return "Sudah ditandai terkirim ke vendor.";
    if (!b.status) return "Belum dicocokkan — tekan Cocokkan dulu.";
    return `Hanya baris "${labelStatus[SIAP_CLAIM] || "Siap Claim"}" yang bisa ditandai.`;
  };

  const jalankanCocok = async () => {
    setSibuk("cocok"); setGagal([]);
    try {
      const r = await api.post(`${MDR_BASE}/tab/${tab}/cocokkan`);
      setRingkas(r.data);
      setPilih(new Set());
      toast.success(`${r.data.diproses || 0} baris dicocokkan`);
      muatUlang();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mencocokkan");
    } finally { setSibuk(""); }
  };

  /** Ekspor butuh HEADER responsnya (`X-MDR-Jumlah`, `X-MDR-Dilewati`), jadi
   *  tidak memakai `downloadBlob` milik keluarga cashback — penolong itu
   *  membuang respons dan dengan begitu membuang jumlah baris yang DILEWATI.
   *  Angka itu yang mencegah ada yang mengira berkasnya lengkap. */
  const unduhEkspor = async () => {
    setSibuk("ekspor");
    try {
      const res = await api.get(`${MDR_BASE}/tab/${tab}/ekspor`, { responseType: "blob" });
      const jumlah = Number(res.headers["x-mdr-jumlah"] ?? NaN);
      const dilewati = Number(res.headers["x-mdr-dilewati"] ?? NaN);
      if (jumlah === 0) {
        toast.warning("Tidak ada baris Siap Claim — berkasnya kosong.");
      }
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = `claim-mdr-${tab}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      if (jumlah > 0) {
        toast.success(Number.isFinite(dilewati) && dilewati > 0
          ? `${jumlah} baris diekspor — ${dilewati} baris DILEWATI karena belum Siap Claim`
          : `${jumlah} baris diekspor`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengekspor");
    } finally { setSibuk(""); }
  };

  const tandai = async () => {
    const ids = terpilih.map((b) => b.kunci);
    if (!ids.length) return;
    setSibuk("tandai");
    try {
      const r = await api.post(`${MDR_BASE}/tab/${tab}/tandai-sudah-claim`, { claim_ids: ids });
      const berhasil = (r.data.berhasil || []).length;
      const g = r.data.gagal || [];
      setGagal(g);
      setPilih(new Set());
      if (berhasil) toast.success(`${berhasil} baris ditandai Sudah Claim`);
      // Kegagalan TIDAK ditelan: satu baris yang gagal dikunci berarti data
      // Price Protection-nya belum tentu terpakai, dan Finance harus tahu
      // baris mana supaya bisa mencobanya lagi (butir 21).
      if (g.length) toast.error(`${g.length} baris tidak bisa ditandai — lihat rinciannya di bawah`);
      muatUlang();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menandai");
    } finally { setSibuk(""); }
  };

  if (!bolehLihat) {
    return (
      <div className="p-6" data-testid="mdr-tanpa-izin">
        <p className="text-sm text-slate-600">Kamu belum punya izin membuka Claim MDR.</p>
      </div>
    );
  }

  const kunciPilihSemua = siap.length > 0 && pilih.size >= siap.length;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="mdr-halaman">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Percent className="w-5 h-5 text-slate-400" /> Claim MDR
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Satu Data Claim dipakai bersama; proses dan statusnya terpisah per tab.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/finance/claim-mdr/srp" data-testid="mdr-ke-srp"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Tags className="w-4 h-4" /> Master SRP Promo
          </Link>
          {bolehUbah && (
            <button onClick={() => setBukaLeasing(true)} data-testid="mdr-buka-leasing"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Settings2 className="w-4 h-4" /> Daftar Leasing
            </button>
          )}
          {bolehUbah && (
            <button onClick={() => setBuka(true)} data-testid="mdr-buka-impor"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Upload className="w-4 h-4" /> Impor Data Claim
            </button>
          )}
          {bolehUbah && (
            <button onClick={jalankanCocok} disabled={!!sibuk} data-testid="mdr-cocokkan"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${sibuk === "cocok" ? "animate-spin" : ""}`} /> Cocokkan
            </button>
          )}
          <button onClick={unduhEkspor} disabled={!!sibuk} data-testid="mdr-ekspor"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
            <FileSpreadsheet className="w-4 h-4" /> Ekspor Claim
          </button>
        </div>
      </div>

      {/* Tab TAM / GDN — labelnya dari server (`aturan.LABEL_TAB`). */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="mdr-tab">
        {(meta?.tab || []).map((t) => (
          <button key={t.kode} onClick={() => setTab(t.kode)} data-testid={`mdr-tab-${t.kode}`}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${
              tab === t.kode ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {ringkas && (
        <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 flex flex-wrap items-center gap-x-4 gap-y-1"
          data-testid="mdr-ringkas">
          <span className="font-medium">{ringkas.diproses} baris dicocokkan</span>
          {Object.entries(ringkas.per_status || {}).filter(([, n]) => n > 0).map(([kode, n]) => (
            <span key={kode}>{labelStatus[kode] || kode}: <b>{n}</b></span>
          ))}
          {ringkas.dilewati_sudah_claim > 0 && (
            <span className="text-slate-500">({ringkas.dilewati_sudah_claim} dilewati karena sudah di-claim)</span>
          )}
        </div>
      )}

      {gagal.length > 0 && (
        <div className="px-4 py-3 bg-rose-50 border border-rose-200 rounded-lg" data-testid="mdr-gagal">
          <p className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {gagal.length} baris tidak bisa ditandai
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {gagal.map((g) => (
              <li key={g.claim_id} className="text-xs text-rose-700">
                <span className="font-mono">{String(g.claim_id).slice(0, 8)}</span> — {g.sebab}
              </li>
            ))}
          </ul>
        </div>
      )}

      {bolehUbah && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            {siap.length} baris Siap Claim{pilih.size > 0 ? ` · ${pilih.size} dipilih` : ""}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-400 hidden sm:inline">
              Tandai SESUDAH berkas ekspor benar-benar dikirim ke vendor
            </span>
            <button onClick={tandai} disabled={!!sibuk || terpilih.length === 0} data-testid="mdr-tandai"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
              <CheckCircle2 className="w-4 h-4" /> Tandai Sudah Claim
            </button>
          </div>
        </div>
      )}

      <BatasDaftarNotice shown={items.length} batas={BATAS_BARIS} testid="mdr-batas-notice" />

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <TabelDesktop>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                {bolehUbah && (
                  <th className="px-3 py-2 w-9">
                    <input type="checkbox" checked={kunciPilihSemua} onChange={toggleSemua}
                      disabled={siap.length === 0} data-testid="mdr-pilih-semua"
                      aria-label="Pilih semua baris Siap Claim" />
                  </th>
                )}
                {/* Butir 7 brief 22 Sep minta 12 kolom. Dua tanggal digabung satu
                    sel dua baris (idiom yang sama dengan IMEI/SN di bawah) supaya
                    tabelnya tidak melebar sampai perlu digeser — yang penting
                    angkanya TERLIHAT, dan sampai 31 Okt 2026 tiga di antaranya
                    (Tgl Aktivasi, Tenor, Nama Konsumen) tidak tampil sama sekali
                    padahal datanya sudah ada dan dipakai mencetak nota. */}
                <th className="px-3 py-2 text-left font-medium">Tgl Beli / Aktivasi</th>
                <th className="px-3 py-2 text-left font-medium">Leasing / CC</th>
                <th className="px-3 py-2 text-left font-medium">Tipe Produk</th>
                <th className="px-3 py-2 text-right font-medium">Tenor</th>
                <th className="px-3 py-2 text-right font-medium">Nominal Kredit</th>
                <th className="px-3 py-2 text-right font-medium">SRP Promo</th>
                <th className="px-3 py-2 text-right font-medium">DP</th>
                <th className="px-3 py-2 text-left font-medium">IMEI / SN</th>
                <th className="px-3 py-2 text-left font-medium">Nama Konsumen</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Nota</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={loadError} kosong={!loading && items.length === 0}
                onRetry={muatUlang} varian="tabel" kolom={bolehUbah ? 12 : 11} data-testid="mdr-state">
                {items.map((b) => {
                  const sebab = sebabTidakBisaDipilih(b);
                  return (
                    <tr key={b.kunci} className="hover:bg-slate-50/60">
                      {bolehUbah && (
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={pilih.has(b.kunci)} disabled={!!sebab}
                            onChange={() => togglePilih(b.kunci)} title={sebab}
                            data-testid={`mdr-pilih-${b.kunci}`} aria-label="Pilih baris ini" />
                        </td>
                      )}
                      <td className="px-3 py-2 whitespace-nowrap leading-tight">
                        <div>{formatTanggal(nilaiMdr(b, "tanggal_pembelian"))}</div>
                        <div className="text-slate-400 text-[11px]">
                          {nilaiMdr(b, "tanggal_aktivasi")
                            ? formatTanggal(nilaiMdr(b, "tanggal_aktivasi")) : "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2">{namaLeasing[nilaiMdr(b, "leasing")] || nilaiMdr(b, "leasing") || "—"}</td>
                      <td className="px-3 py-2 max-w-[18rem] truncate" title={nilaiMdr(b, "tipe_produk") || ""}>
                        {nilaiMdr(b, "tipe_produk") || "—"}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{teksTenor(b)}</td>
                      <td className="px-3 py-2 text-right font-mono">{idr(nilaiMdr(b, "nominal"))}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {nilaiMdr(b, "srp") == null ? "—" : idr(nilaiMdr(b, "srp"))}
                        {b.hasil?.sumber_srp === "master" && (
                          <span className="ml-1 text-[10px] text-slate-400" title="Diambil dari Master SRP Promo">M</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {nilaiMdr(b, "dp") == null ? "—" : idr(nilaiMdr(b, "dp"))}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] leading-tight">
                        <div>{nilaiMdr(b, "imei") || "—"}</div>
                        <div className="text-slate-400">{nilaiMdr(b, "sn") || "—"}</div>
                      </td>
                      <td className="px-3 py-2 max-w-[12rem] truncate" title={nilaiMdr(b, "nama_konsumen") || ""}>
                        {nilaiMdr(b, "nama_konsumen") || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Pil kode={b.status} label={labelStatus[b.status]} />
                        <AlasanBaris baris={b} pesanAlasan={pesanAlasan} namaMedan={namaMedan} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <TombolNota baris={b} kurang={medanKurangNota(b)} namaMedan={namaMedan}
                          onCetak={() => setNotaBaris(b)} />
                      </td>
                    </tr>
                  );
                })}
              </LoadingState>
            </tbody>
          </table>
        </TabelDesktop>

        <DaftarKartuMobile testid="mdr-kartu">
          <LoadingState loading={loading} error={loadError} kosong={!loading && items.length === 0}
            onRetry={muatUlang} varian="kartu" data-testid="mdr-state-m">
            {items.map((b) => {
              const sebab = sebabTidakBisaDipilih(b);
              return (
                <KartuBaris key={b.kunci} testid={`mdr-card-${b.kunci}`}>
                  <KartuBarisHeader
                    kiri={
                      <div className="flex items-start gap-2">
                        {bolehUbah && (
                          <input type="checkbox" checked={pilih.has(b.kunci)} disabled={!!sebab}
                            onChange={() => togglePilih(b.kunci)} title={sebab} className="mt-1"
                            data-testid={`mdr-pilih-${b.kunci}-m`} aria-label="Pilih baris ini" />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {nilaiMdr(b, "tipe_produk") || "Tipe belum diketahui"}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {formatTanggal(nilaiMdr(b, "tanggal_pembelian"))} ·{" "}
                            {namaLeasing[nilaiMdr(b, "leasing")] || nilaiMdr(b, "leasing") || "—"}
                          </p>
                        </div>
                      </div>
                    }
                    kanan={<Pil kode={b.status} label={labelStatus[b.status]} />}
                  />
                  {/* Kartu dan tabel dirender BERSAMAAN (responsive-table), jadi
                      tiga medan butir 7 harus ditambahkan di KEDUANYA — kalau cuma
                      di satu sisi, pengguna HP atau pengguna desktop tetap tidak
                      melihatnya dan tesnya bisa hijau palsu (CLAUDE.md). */}
                  <KartuBarisMeta label="Tgl Aktivasi" value={nilaiMdr(b, "tanggal_aktivasi")
                    ? formatTanggal(nilaiMdr(b, "tanggal_aktivasi")) : ""} />
                  <KartuBarisMeta label="Tenor" value={teksTenor(b) === "—" ? "" : teksTenor(b)} />
                  <KartuBarisMeta label="Nominal Kredit" value={idr(nilaiMdr(b, "nominal"))} tebal />
                  <KartuBarisMeta label="SRP Promo" value={nilaiMdr(b, "srp") == null ? "" : idr(nilaiMdr(b, "srp"))} />
                  <KartuBarisMeta label="DP" value={nilaiMdr(b, "dp") == null ? "" : idr(nilaiMdr(b, "dp"))} tebal />
                  <KartuBarisMeta label="IMEI" value={nilaiMdr(b, "imei")} />
                  <KartuBarisMeta label="SN" value={nilaiMdr(b, "sn")} />
                  <KartuBarisMeta label="Nama Konsumen" value={nilaiMdr(b, "nama_konsumen")} />
                  <AlasanBaris baris={b} pesanAlasan={pesanAlasan} namaMedan={namaMedan} />
                  <KartuBarisAksi>
                    <TombolNota baris={b} kurang={medanKurangNota(b)} namaMedan={namaMedan}
                      onCetak={() => setNotaBaris(b)} akhiran="-m" />
                  </KartuBarisAksi>
                </KartuBaris>
              );
            })}
          </LoadingState>
        </DaftarKartuMobile>
      </div>

      {notaBaris && (
        <ClaimMdrNotaPrint
          baris={notaBaris}
          tab={tab}
          labelTab={(meta?.tab || []).find((t) => t.kode === tab)?.label}
          namaLeasing={namaLeasing}
          onTutup={() => setNotaBaris(null)}
        />
      )}

      {bukaLeasing && (
        <ClaimMdrLeasingModal
          tab={meta?.tab || []}
          leasing={meta?.leasing || []}
          onTutup={() => setBukaLeasing(false)}
          onSimpan={(items) => {
            // Daftar leasing menentukan tab mana yang memproses sebuah baris,
            // jadi kosakata layar DAN hasil pencocokan ikut berubah artinya.
            // Meta disegarkan dari balasan server (yang BENAR-BENAR tersimpan),
            // dan barisnya dimuat ulang supaya nama leasing tidak basi.
            setMeta((m) => (m ? { ...m, leasing: items } : m));
            muatUlang();
          }}
        />
      )}

      {buka && (
        <ClaimMdrImporModal
          leasing={meta?.leasing || []}
          medanImpor={meta?.medan_impor || []}
          onTutup={() => setBuka(false)}
          onSelesai={muatUlang}
        />
      )}
    </div>
  );
}

/** Tombol "Cetak Nota" satu baris (butir 28).
 *
 * Mati dengan SEBAB TERTULIS kalau medannya belum lengkap — bukan hilang
 * tanpa penjelasan, dan bukan hidup lalu mencetak nota berlubang. Teks
 * sebabnya memakai nama medan dari `/meta`, sama dengan yang dipakai alasan
 * kegagalan pencocokan.
 */
function TombolNota({ baris, kurang, namaMedan, onCetak, akhiran = "" }) {
  const mati = kurang.length > 0;
  const sebab = mati
    ? `Belum bisa dicetak — ${kurang.map((m) => namaMedan[m] || m).join(", ")} masih kosong.`
    : "";
  return (
    <button onClick={onCetak} disabled={mati} title={sebab}
      data-testid={`mdr-cetak-nota-${baris.kunci}${akhiran}`}
      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
      <Printer className="w-3 h-3" /> Nota
    </button>
  );
}

/** Alasan gagal + medan mana yang kosong.
 *
 * Kalimatnya SELALU dari `/meta` (`aturan.PESAN_ALASAN` + `cocok.PESAN_TAMBAHAN`)
 * dan nama medannya dari `aturan.LABEL_MEDAN`. Tanpa `rincian.medan_kurang`,
 * "Data belum lengkap" tidak memberi tahu apa pun yang bisa dikerjakan — itu
 * sebabnya rinciannya ikut disimpan di `simpan.tulis_proses`. */
function AlasanBaris({ baris, pesanAlasan, namaMedan }) {
  const alasan = baris.alasan || [];
  const kurang = baris.rincian?.medan_kurang || [];
  const ganda = baris.rincian?.kandidat || [];
  if (!alasan.length && !kurang.length) return null;
  return (
    <div className="mt-1 space-y-0.5" data-testid={`mdr-alasan-${baris.kunci}`}>
      {alasan.map((a) => (
        <p key={a} className="text-[11px] text-slate-500 leading-snug">{pesanAlasan[a] || a}</p>
      ))}
      {kurang.length > 0 && (
        <p className="text-[11px] text-slate-400 leading-snug">
          Belum terisi: {kurang.map((m) => namaMedan[m] || m).join(", ")}
        </p>
      )}
      {ganda.length > 1 && (
        <p className="text-[11px] text-slate-400 leading-snug">{ganda.length} nota sama-sama cocok</p>
      )}
    </div>
  );
}
